import { useQuery } from '@tanstack/react-query'
import { settingsApi } from '../../api/settings'
import { formatCost } from '../../lib/format'
import type { ModelSpend, SpendBand, SpendLine } from '../../types/settings'

// THE MODEL SPEND LEDGER (REQ-110). Don, stand-up 2026-09-25: "you're still counting all the tokens. So
// when we have to switch over, you're going to know the dollar amount that it's going to cost us" — "I am
// going to know that." Scout runs its model turns through a subsidised plan today; every turn's tokens and
// its list-price cost are read off the wire and kept, and this is where they add up. Presentation only: the
// API sums the ledger; nothing here estimates a price.

const SURFACE_WORDS: Record<string, string> = {
  'scout.workbench-chat': 'Wordsmith',
  'scout.voice-draft': 'Drafts',
  'scout.voice-describe': 'Voice profile',
  'scout.voice-verify': 'Draft checks',
  'scout.workbench-challenge': 'Challenger',
}

function surfaceWords(key: string): string {
  return SURFACE_WORDS[key] ?? key.replace(/^scout\./, '').replace(/-/g, ' ')
}

function modelWords(key: string): string {
  return key.replace(/^claude-cli:/, '').replace(/^claude-/, '').replace(/-(\d)-(\d)$/, ' $1.$2').replace(/^\w/, (c) => c.toUpperCase())
}

const tokens = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

function Band({ band, title }: { band: SpendBand; title: string }) {
  const unpriced = band.turns - band.meteredTurns
  return (
    <div className="spend-band">
      <span className="muted small">{title}</span>
      <strong>{formatCost(band.costUsd)}</strong>
      <span className="small">{band.turns} {band.turns === 1 ? 'turn' : 'turns'}{unpriced > 0 ? ` · ${unpriced} unpriced` : ''}</span>
      <span className="muted small">{tokens.format(band.inputTokens + band.cacheReadTokens + band.cacheWriteTokens)} in · {tokens.format(band.outputTokens)} out</span>
    </div>
  )
}

function Lines({ lines, words, heading }: { lines: SpendLine[]; words: (k: string) => string; heading: string }) {
  if (lines.length === 0) return null
  return (
    <table className="spend-lines">
      <thead><tr><th>{heading}</th><th>Turns</th><th>Tokens in</th><th>Tokens out</th><th>Cost</th></tr></thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.key}>
            <td>{words(l.key)}</td>
            <td>{l.turns}</td>
            <td>{tokens.format(l.inputTokens)}</td>
            <td>{tokens.format(l.outputTokens)}</td>
            <td>{formatCost(l.costUsd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function Spend({ spoke }: { spoke: string }) {
  const q = useQuery({ queryKey: ['settings-spend', spoke], queryFn: () => settingsApi.spend(spoke), retry: false, staleTime: 60_000 })
  const spend: ModelSpend | undefined = q.data
  const since = spend?.firstMeteredAt ? new Date(spend.firstMeteredAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null
  const unmetered = spend ? spend.allTime.turns - spend.allTime.meteredTurns : 0
  return (
    <section className="card setting-section spend">
      <div className="card-head">
        <div>
          <h2>Model spend</h2>
          <p className="muted">
            What the firm&apos;s model turns would cost at the provider&apos;s list price. Scout runs them through a subsidised
            plan today; this is the number for the day it does not.
          </p>
        </div>
      </div>
      {q.isPending && <p className="muted small">Adding up…</p>}
      {q.isError && <p className="muted small">The ledger could not be read: {q.error.message}</p>}
      {spend && (
        <>
          <div className="spend-bands">
            <Band band={spend.today} title="Today" />
            <Band band={spend.week} title="Last 7 days" />
            <Band band={spend.month} title="Last 30 days" />
            <Band band={spend.allTime} title="All time" />
          </div>
          <Lines lines={spend.bySurface} words={surfaceWords} heading="Last 30 days, by surface" />
          <Lines lines={spend.byModel} words={modelWords} heading="Last 30 days, by model" />
          <p className="muted small">
            {since ? `Priced from ${since}. ` : 'No turn has been priced yet. '}
            {unmetered > 0 ? `${unmetered} earlier ${unmetered === 1 ? 'turn was' : 'turns were'} recorded before metering: counted, not priced.` : ''}
          </p>
        </>
      )}
    </section>
  )
}
